import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "@/lib/api";
import i18n from "@/i18n";

export const lokaleAccountsKey = ["beheer", "lokale-accounts"] as const;

/**
 * Which slice of the list the screen is asking for. The two narrow ones used to be a screen of
 * their own - Koppelingen - over the same table. ADR-0018 flags a known-wrong link so somebody
 * checks it, and a flag in a list of every account is not checkable, so the set stays reachable
 * as a set.
 */
export type AccountFilter = "alle" | "wacht" | "afwijkend";

export const lokaleAccountsPagedKey = (
  filter: AccountFilter,
  deur: string | undefined,
  status: string | undefined,
  page: number | undefined,
  search: string,
  sortBy: string | undefined,
  sortDescending: boolean,
) =>
  [
    ...lokaleAccountsKey,
    "paged",
    filter,
    deur,
    status,
    page,
    search,
    sortBy,
    sortDescending,
  ] as const;

/**
 * Every credential, whichever door it belongs to, with the state of its inloglink and of its link
 * to a persoon. Beheerder-only on the API, so a typed route shows a korpscontact nothing.
 */
export function useLokaleAccountsPaged(
  filter: AccountFilter,
  page: number | undefined,
  pageSize: number,
  search: string,
  sortBy: string | undefined,
  sortDescending: boolean,
  // #194's own filters: the two columns the list derives per row rather than searches.
  deur?: string,
  status?: string,
) {
  return useQuery({
    queryKey: lokaleAccountsPagedKey(
      filter,
      deur,
      status,
      page,
      search,
      sortBy,
      sortDescending,
    ),
    queryFn: async () => {
      const { data, response } = await api.GET("/api/beheer/lokale-accounts", {
        params: {
          query: {
            // Absent rather than "alle": the server reads anything else as the whole list, and a
            // default that is not in the URL keeps the plain screen's address plain.
            filter: filter === "alle" ? undefined : filter,
            deur,
            status,
            page,
            pageSize,
            search: search || undefined,
            sortBy,
            sortDescending,
          },
        },
      });
      if (!data)
        throw new Error(
          i18n.t("lokaleAccounts:errors.ophalen", { status: response.status }),
        );
      return data;
    },
    // Each page is its own query key, so without this a click to a page never fetched before
    // clears `data` and swaps the table for a skeleton - unmounting the very pager button just
    // clicked, which loses focus and (in some browsers) scrolls the page back to the top. The
    // previous page's rows stay on screen, correct but stale, until the new ones arrive.
    placeholderData: keepPreviousData,
  });
}

export function useIssueLokaalAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      email,
      persoonId,
    }: {
      email: string;
      persoonId?: string;
    }) => {
      const { data, error, response } = await api.POST(
        "/api/beheer/lokale-accounts",
        {
          // Explicitly null rather than absent: the generated contract makes persoonId nullable,
          // and "no persoon named" is what triggers the exactly-one auto-bind on the server.
          body: { email, persoonId: persoonId ?? null },
        },
      );

      if (!data) {
        const problem = error as { error?: string } | undefined;
        throw new Error(
          problem?.error ??
            i18n.t("lokaleAccounts:errors.aanmaken", {
              status: response.status,
            }),
        );
      }

      return data;
    },
    onSuccess: (data) => {
      // "kies-persoon" created nothing - refetching would only hide the choice being asked for.
      if (data.status !== "kies-persoon") {
        void queryClient.invalidateQueries({ queryKey: lokaleAccountsKey });
      }
    },
  });
}

/**
 * The beheerder never gives the address: one they could choose would let them mail themselves a
 * session as anybody. See docs/adr/0066-an-inloglink-is-the-fourth-door-and-a-password-becomes-optional.md.
 */
export function useSendInloglink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { response } = await api.POST(
        "/api/beheer/lokale-accounts/{id}/inloglink",
        {
          params: { path: { id } },
        },
      );
      if (!response.ok)
        throw new Error(
          i18n.t("lokaleAccounts:errors.versturen", {
            status: response.status,
          }),
        );
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: lokaleAccountsKey }),
  });
}

/** The answer to "ik heb die mail per ongeluk doorgestuurd". Links are consumed per nonce. */
export function useRevokeInloglinks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { response } = await api.DELETE(
        "/api/beheer/lokale-accounts/{id}/inloglink",
        {
          params: { path: { id } },
        },
      );
      if (!response.ok)
        throw new Error(
          i18n.t("lokaleAccounts:errors.intrekken", {
            status: response.status,
          }),
        );
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: lokaleAccountsKey }),
  });
}

/** The server writes the refusal (the last writer, a non-beheerder), so its title wins over the catalog. */
function refusal(error: unknown, fallback: string) {
  const title = (error as { title?: string } | undefined)?.title;
  return new Error(title ?? fallback);
}

/** Lets the persoon behind an account change things, or limits them to reading (ADR-0078). */
export function useSetRights() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      hasWriteAccess,
    }: {
      id: string;
      hasWriteAccess: boolean;
    }) => {
      const { error, response } = await api.PUT(
        "/api/beheer/lokale-accounts/{id}/rechten",
        {
          params: { path: { id } },
          body: { hasWriteAccess },
        },
      );
      if (!response.ok) {
        throw refusal(
          error,
          i18n.t("lokaleAccounts:errors.rechten", { status: response.status }),
        );
      }
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: lokaleAccountsKey }),
  });
}

/** Removes the credential only; the persoon it belonged to stays. */
export function useDeleteAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await api.DELETE(
        "/api/beheer/lokale-accounts/{id}",
        {
          params: { path: { id } },
        },
      );
      if (!response.ok) {
        throw refusal(
          error,
          i18n.t("lokaleAccounts:errors.verwijderen", {
            status: response.status,
          }),
        );
      }
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: lokaleAccountsKey }),
  });
}

/**
 * Drops the password and the second factor, and mails a link. It cannot clear the factor alone
 * any more: a password without one is the row the check constraint forbids.
 */
export function useResetCredential() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { response } = await api.POST(
        "/api/beheer/lokale-accounts/{id}/tweede-factor",
        {
          params: { path: { id } },
        },
      );
      if (!response.ok)
        throw new Error(
          i18n.t("lokaleAccounts:errors.resetten", { status: response.status }),
        );
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: lokaleAccountsKey }),
  });
}

/** Exported for the `status` filter's own `opties` (#194) - the same six values the column shows. */
export const STATUSSEN = [
  "actief",
  "nooit-aangemeld",
  "inloglink-verstuurd",
  "inloglink-verlopen",
  "tweede-factor-geblokkeerd",
  "geblokkeerd",
] as const;

/** Exported for the `deur` filter's own `opties` (#194). */
export const DEUREN = ["provider", "inloglink", "wachtwoord"] as const;

const BRONNEN = ["portaal", "vbs"] as const;

/**
 * JBNL's wording for a value the API reports, or the value itself where this build has no
 * wording for it yet. Read at call time: a table would be built before i18next has initialised.
 */
function label(
  group: string,
  allowed: readonly string[],
  value: string | null | undefined,
) {
  if (!value) return "";

  return allowed.includes(value)
    ? i18n.t(
        `lokaleAccounts:${group}.${value}` as "lokaleAccounts:status.actief",
      )
    : value;
}

export const accountStatusLabel = (status: string | null | undefined) =>
  label("status", STATUSSEN, status);

/** Which door this account signs in with - the column the old screen could not show at all. */
export const accountDeurLabel = (deur: string | null | undefined) =>
  label("deur", DEUREN, deur);

/** Which system holds the address, because the correction for a wrong one lives there. */
export const accountBronLabel = (bron: string | null | undefined) =>
  label("bron", BRONNEN, bron);
